import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  styled,
  CircularProgress,
  Chip,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

// Interfaces
interface FeedbackType {
  id: string;
  name: string;
  type: string;
  description?: string;
}

interface FirestoreUser {
  first_name: string;
  last_name: string;
  email: string;
}

interface UserFeedback {
  id: string;
  user_id: string;
  rating: number;
  feedback_type_id: string;
  feedback: string | null;
  date: any; // Firestore Timestamp
}

interface FeedbackWithDetails extends UserFeedback {
  userName: string;
  feedbackTypeName: string;
}

// Styled Components
const MainContent = styled('div')({
  backgroundColor: '#ffffff',
  padding: '0px 10px',
  '@media (max-width: 1024px)': {
    padding: '0px 16px',
  }
});

const StyledTableContainer = styled(TableContainer)({
  marginTop: '16px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  overflowX: 'auto',
  '@media (max-width: 1024px)': {
    maxWidth: '100vw',
    minWidth: '600px',
    padding: 0,
  },
  '& .MuiTable-root': {
    minWidth: '800px',
    '@media (max-width: 1024px)': {
      minWidth: '600px',
    },
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#ffd999',
    fontWeight: 'bold',
    color: '#000000',
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .MuiTableCell-body': {
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .MuiTableCell-body:nth-of-type(4)': { // Feedback column
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: '600px',
    '@media (max-width: 1024px)': {
      maxWidth: '400px',
    }
  }
});

const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '4px',
    height: '40px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#bdbdbd',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2196F3',
    },
    '@media (max-width: 1024px)': {
      height: '48px',
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 14px',
    fontSize: '14px',
    '@media (max-width: 1024px)': {
      padding: '12px 16px',
      fontSize: '16px',
    }
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      fontSize: '24px',
      color: '#757575',
      '@media (max-width: 1024px)': {
        fontSize: '28px',
      }
    },
  },
});

type Order = 'asc' | 'desc';
type OrderBy = 'date' | 'rating' | 'userName';

export default function UserFeedbackComponent() {
  const [feedbacks, setFeedbacks] = useState<FeedbackWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>('date');
  const [order, setOrder] = useState<Order>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch feedback types first
      const typesRef = collection(db, 'feedback_type');
      const typesSnapshot = await getDocs(typesRef);
      const typesData: { [key: string]: FeedbackType } = {};
      typesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        typesData[doc.id] = {
          id: doc.id,
          name: data.name || '',
          type: data.type || '',
          description: data.description
        };
      });

      // Fetch user feedback
      const feedbacksRef = collection(db, 'user_feedback');
      const feedbackSnapshot = await getDocs(feedbacksRef);
      
      // Fetch user details for each feedback
      const feedbackPromises = feedbackSnapshot.docs.map(async (feedbackDoc) => {
        const feedbackData = feedbackDoc.data();
        let userName = 'Unknown User';
        
        try {
          // Handle user_id which could be a DocumentReference or string
          let userId = '';
          if (feedbackData.user_id && typeof feedbackData.user_id === 'object' && feedbackData.user_id.id) {
            userId = feedbackData.user_id.id;
          } else if (typeof feedbackData.user_id === 'string') {
            userId = feedbackData.user_id;
          }

          if (userId) {
            // First try to get from users collection
            const userDoc = await getDoc(doc(db, 'users', userId));
            
            if (userDoc.exists()) {
              const userData = userDoc.data() as FirestoreUser;
              const firstName = userData.first_name || '';
              const lastName = userData.last_name || '';
              userName = `${firstName} ${lastName}`.trim() || 'Unknown User';
            } else {
              // If not found in users collection, try admin_users collection
              const adminUserDoc = await getDoc(doc(db, 'admin_users', userId));
              
              if (adminUserDoc.exists()) {
                const adminUserData = adminUserDoc.data() as FirestoreUser;
                const firstName = adminUserData.first_name || '';
                const lastName = adminUserData.last_name || '';
                userName = `${firstName} ${lastName}`.trim() || 'Unknown User';
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
        
        // Handle feedback_type_id
        const feedbackTypeId = feedbackData.feedback_type_id;
        const feedbackType = typesData[feedbackTypeId];
        
        return {
          id: feedbackDoc.id,
          user_id: feedbackData.user_id || '',
          rating: feedbackData.rating || 0,
          feedback_type_id: feedbackData.feedback_type_id || '',
          feedback: feedbackData.feedback || null,
          date: feedbackData.submitted_date,
          userName: userName,
          feedbackTypeName: feedbackType?.type || 'Unknown Type'
        } as FeedbackWithDetails;
      });

      const feedbacksData = await Promise.all(feedbackPromises);
      setFeedbacks(feedbacksData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredFeedbacks = feedbacks
    .filter(feedback => {
      const searchLower = searchQuery.toLowerCase();
      return (
        (feedback.userName.toLowerCase() || '').includes(searchLower) ||
        (feedback.feedback?.toLowerCase() || '').includes(searchLower)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      
      switch (orderBy) {
        case 'date':
          return isAsc
            ? (a.date?.seconds || 0) - (b.date?.seconds || 0)
            : (b.date?.seconds || 0) - (a.date?.seconds || 0);
        case 'rating':
          return isAsc
            ? (a.rating || 0) - (b.rating || 0)
            : (b.rating || 0) - (a.rating || 0);
        case 'userName':
          return isAsc
            ? (a.userName || '').localeCompare(b.userName || '')
            : (b.userName || '').localeCompare(a.userName || '');
        default:
          return 0;
      }
    });

  return (
    <MainContent>
      <Box>
        <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '1.5rem' }}>
          Manage Feedback
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <SearchTextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '24px' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: '300px' }}
        />

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'userName'}
                    direction={orderBy === 'userName' ? order : 'asc'}
                    onClick={() => handleRequestSort('userName')}
                  >
                    User Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rating'}
                    direction={orderBy === 'rating' ? order : 'asc'}
                    onClick={() => handleRequestSort('rating')}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell>Feedback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress sx={{ color: '#FF7043' }} />
                  </TableCell>
                </TableRow>
              ) : filteredFeedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {searchQuery ? 'No feedback found matching your search' : 'No feedback found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFeedbacks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>{formatDate(feedback.date)}</TableCell>
                      <TableCell>{feedback.userName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={feedback.rating || 'N/A'}
                          size="small"
                          sx={{ 
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '600px'
                      }}>
                        {feedback.feedback || 'No feedback provided'}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredFeedbacks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-selectLabel': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-displayedRows': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-select': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-actions': {
              color: '#424242'
            }
          }}
        />
      </Box>
    </MainContent>
  );
} 